// Back in stock notifications for the product page (snippets/back-in-stock-button.liquid and
// snippets/back-in-stock-modal.liquid). The notify button follows the selected colour and size
// without a page reload; signups go to Klaviyo's client API.
(() => {
  if (customElements.get('back-in-stock-notify')) return;

  const KLAVIYO_API = 'https://a.klaviyo.com/client';
  // The revision and content type Klaviyo's own back in stock widget sends today.
  const KLAVIYO_REVISION = '2024-02-15';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Requests that succeeded in this page view. A repeated submit, or a retry after a partial
  // failure, only sends what is missing, so Klaviyo never gets a duplicate subscription.
  const sent = new Set();

  const post = async (companyId, path, data) => {
    const response = await fetch(`${KLAVIYO_API}/${path}/?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({ data }),
    });
    if (!response.ok) throw new Error(`Klaviyo ${path} returned ${response.status}`);
  };

  class BackInStockNotify extends HTMLElement {
    connectedCallback() {
      if (!this.variants) {
        this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
        this.querySelector('button').addEventListener('click', () => this.open());
      }
      this.sync();
    }

    get selectedVariant() {
      const picker = (this.closest('.shopify-section') || document).querySelector('variant-radios');
      if (picker) {
        const options = [...picker.querySelectorAll('fieldset')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
        if (options.includes(undefined)) return null;
        return this.variants.find((variant) => variant.options.every((option, index) => option === options[index])) || null;
      }
      const id = this.closest('form')?.querySelector('input[name="id"]')?.value;
      return this.variants.find((variant) => String(variant.id) === id) || (this.variants.length === 1 ? this.variants[0] : null);
    }

    sync() {
      const variant = this.selectedVariant;
      this.hidden = !variant || variant.available;
      return this.hidden ? null : variant;
    }

    open() {
      const variant = this.sync();
      const modal = document.getElementById(this.dataset.modal);
      if (!variant || !modal?.show) return;
      modal.querySelector('back-in-stock-form')?.prepare(variant);
      modal.show(this.querySelector('button'));
    }
  }

  class BackInStockForm extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('form');
      this.fields = this.form.querySelector('fieldset');
      this.email = this.form.elements.email;
      this.newsletter = this.form.elements.newsletter;
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.message = this.querySelector('[role="alert"]');
      this.success = this.querySelector('[role="status"]');
      this.footer = this.querySelector('.back-in-stock-modal__footer');
      this.variantText = this.querySelector('[data-back-in-stock-variant]');
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
      this.email.addEventListener('input', () => {
        if (this.error === 'email') this.showError(null);
      });
      this.newsletter.addEventListener('change', () => {
        if (this.error === 'newsletter' && this.newsletter.checked) this.showError(null);
      });
      const toggle = this.querySelector('[aria-expanded][aria-controls]');
      toggle?.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(expanded));
        document.getElementById(toggle.getAttribute('aria-controls')).hidden = !expanded;
      });
    }

    prepare(variant) {
      this.variant = variant;
      this.variantText.textContent = variant.label;
      this.variantText.hidden = !variant.label;
      this.showError(null);
      this.fields.disabled = false;
      this.form.hidden = false;
      this.success.textContent = '';
      this.footer.hidden = false;
    }

    showError(key, field) {
      this.error = key;
      this.message.textContent = key ? this.dataset[`error${key[0].toUpperCase()}${key.slice(1)}`] : '';
      if (key === 'email') this.email.setAttribute('aria-invalid', 'true');
      else this.email.removeAttribute('aria-invalid');
      if (field) field.focus();
    }

    showSuccess() {
      // The status region stays in the page, so screen readers announce the new text.
      this.success.textContent = this.dataset.success;
      this.fields.disabled = true;
      this.form.hidden = true;
      this.footer.hidden = true;
      // Keep focus inside the popup now that the form controls are gone.
      const modal = this.closest('modal-dialog');
      const close = modal.querySelector('[id^="ModalClose-"]');
      if (typeof window.trapFocus === 'function') window.trapFocus(modal, close);
      else close.focus();
    }

    async submit() {
      if (this.pending || !this.variant) return;
      const email = this.email.value.trim();
      if (!EMAIL_PATTERN.test(email)) return this.showError('email', this.email);
      if (this.newsletter.required && !this.newsletter.checked) return this.showError('newsletter', this.newsletter);
      this.showError(null);

      const { companyId, listId } = this.dataset;
      const variantId = this.variant.id;
      const address = email.toLowerCase();
      const requests = [[`variant:${address}:${variantId}`, () => post(companyId, 'back-in-stock-subscriptions', {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email } } },
          channels: ['EMAIL'],
        },
        relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } } },
      })]];
      if (this.newsletter.checked && listId) {
        requests.push([`list:${address}:${listId}`, () => post(companyId, 'subscriptions', {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email, properties: { $source: 'Back in Stock' } } } },
          },
          relationships: { list: { data: { type: 'list', id: listId } } },
        })]);
      }

      this.pending = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.setAttribute('aria-busy', 'true');
      try {
        const results = await Promise.allSettled(requests.filter(([key]) => !sent.has(key)).map(async ([key, send]) => {
          await send();
          sent.add(key);
        }));
        // The popup may have been reopened for another size meanwhile; the result
        // only belongs on screen while the same variant is shown.
        if (this.variant?.id !== variantId) return;
        const failure = results.find((result) => result.status === 'rejected');
        if (failure) throw failure.reason;
        this.showSuccess();
      } catch (error) {
        console.error('Back in stock signup failed', error);
        if (this.variant?.id === variantId) this.showError('general');
      } finally {
        this.pending = false;
        this.submitButton.removeAttribute('aria-disabled');
        this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('back-in-stock-notify', BackInStockNotify);
  customElements.define('back-in-stock-form', BackInStockForm);

  const syncAll = (root = document) => root.querySelectorAll('back-in-stock-notify').forEach((notify) => notify.sync());
  document.addEventListener('change', (event) => {
    if (event.target.closest?.('variant-radios')) syncAll(event.target.closest('.shopify-section') || document);
  });
  // Colour switches replace the section in place; the size restore of product-color-navigation runs
  // before this event and on page load, and back/forward navigation can restore the radios.
  document.addEventListener('teveo:product:changed', (event) => syncAll(event.detail?.section || document));
  document.addEventListener('DOMContentLoaded', () => syncAll());
  window.addEventListener('load', () => syncAll());
  window.addEventListener('pageshow', () => syncAll());
})();
